import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import { GroupWithRelationsDto, ResponseDto } from "@mimber/schema"
import { groupsKeys } from "../groups.utils"

interface DeleteGroupLogoOptions extends Omit<
  UseMutationOptions<ResponseDto<GroupWithRelationsDto>, Error, void>,
  "mutationFn"
> {}

/**
 * Hook for deleting group logo
 *
 * Removes the logo file from S3 and clears the group's logoUrl.
 *
 * @param groupUuid - Group UUID
 * @param options - Additional options for the mutation
 * @returns Mutation hook for deleting logo
 *
 * @example
 * ```tsx
 * const deleteLogo = useDeleteGroupLogo(groupUuid, {
 *   onSuccess: () => {
 *     toast.success('Logo deleted successfully!')
 *   }
 * })
 *
 * deleteLogo.mutate()
 * ```
 */
export const useDeleteGroupLogo = (
  groupUuid: string,
  options: DeleteGroupLogoOptions = {}
): UseMutationResult<ResponseDto<GroupWithRelationsDto>, Error, void> => {
  guardAppUsage("mimber-organization", "useDeleteGroupLogo")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async () => {
      const response = await server.delete<ResponseDto<GroupWithRelationsDto>>(
        `groups/${groupUuid}/logo`
      )
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      // Invalidate queries to refetch with cleared logo URL
      queryClient.invalidateQueries({
        queryKey: groupsKeys.findAll(),
      })
      queryClient.invalidateQueries({
        queryKey: groupsKeys.findOne(groupUuid),
      })

      if (options?.onSuccess) {
        options.onSuccess(response, variables, onMutateResult, context)
      }
    },
  })
}
